import { useCallback, useEffect, useRef, useState } from "react";
import { FaTimes, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { CONSENT_EVENT, hasChosen } from "../lib/cookieConsent";
import "./AnnouncementPopup.css";

const STORAGE_KEY = "zu-announce-new-degrees-v1";
const APPLY_URL = "https://sajili.zetech.ac.ke";

const PROGRAMMES = [
  // {
  //   name: "Bachelor of Science in Clinical Medicine & Community Health",
  //   slug: "bachelor-of-science-in-clinical-medicine-and-community-health-bcm",
  // },
  // {
  //   name: "Bachelor of Digital Marketing",
  //   slug: "bachelor-of-digital-marketing",
  // },
  // {
  //   name: "Bachelor of Economics and Data Science",
  //   slug: "bachelor-of-economics-and-data-science",
  // },
  {
    name: "Master of Arts in International Relations and Diplomacy",
    slug: "master-of-arts-in-international-relations-and-diplomacy",
  },
  {
    name: "Doctor of Philosophy in International Relations and Diplomacy",
    slug: "doctor-of-philosophy-in-international-relations-and-diplomacy",
  },
  {
    name: "Postgraduate Diploma in Education",
    slug: "postgraduate-diploma-in-education",
  },
];

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export default function AnnouncementPopup({ delay = 1800 }) {
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);
  const cardRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch (_) {
      dismissed = false;
    }
    if (dismissed) return undefined;

    let timer = 0;
    const schedule = (wait) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setOpen(true), wait);
    };

    // The cookie notice comes first; queue the announcement behind it so both
    // are seen instead of stacking on top of each other.
    if (hasChosen()) {
      schedule(delay);
      return () => window.clearTimeout(timer);
    }

    const onConsent = () => schedule(600);
    window.addEventListener(CONSENT_EVENT, onConsent);

    // Safety net: if the visitor ignores the cookie banner, still show it.
    schedule(delay + 12000);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(CONSENT_EVENT, onConsent);
    };
  }, [delay]);

  const dismiss = useCallback(() => {
    setOpen(false);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {
      /* storage unavailable — popup simply shows again next load */
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    lastFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const id = window.setTimeout(() => closeRef.current?.focus(), 80);

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key !== "Tab") return;

      const card = cardRef.current;
      if (!card) return;
      const items = Array.from(card.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!card.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previous;
      window.clearTimeout(id);
      lastFocusedRef.current?.focus?.();
    };
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div className="zu-announce" role="presentation">
      <button
        type="button"
        className="zu-announce-backdrop"
        onClick={dismiss}
        tabIndex={-1}
        aria-hidden="true"
      />

      <div
        className="zu-announce-card"
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="zu-announce-title"
        aria-describedby="zu-announce-desc"
      >

        <div className="zu-announce-head">
          <h2 id="zu-announce-title">Introducing Our New Postgraduate Programmes</h2>
          <span className="zu-announce-rule" aria-hidden="true" />

          <button
            type="button"
            ref={closeRef}
            className="zu-announce-close"
            onClick={dismiss}
            aria-label="Close announcement"
          >
            <FaTimes />
          </button>
        </div>

        <div className="zu-announce-body">
          <p id="zu-announce-desc" className="zu-announce-desc">
            Zetech University has launched three new postgraduate programmes now open for
            application.
          </p>
          <ol className="zu-announce-list">

            {PROGRAMMES.map((programme, i) => (
              <li className="zu-announce-item" key={programme.slug}>
                <Link
                  className="zu-announce-link"
                  to={`/programme/${programme.slug}`}
                  onClick={dismiss}
                >
                  <span className="zu-announce-num" aria-hidden="true">{i + 1}</span>
                  <span className="zu-announce-name">{programme.name}</span>
                  <FaArrowRight className="zu-announce-item-arrow" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ol>

          <a
            className="zu-announce-cta"
            href={APPLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
          >
            Apply Now
            <FaArrowRight aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  );
}
